<template>
    <div class="app_viewport w-full h-full overflow-hidden">
        <div class="app_stage relative">
            <div class="app_shell flex flex-col w-full h-full overflow-hidden">
                <AppHeader />
                <div class="app_body flex overflow-hidden">
                    <AppSidebar />
                    <main id="mainContent" ref="mainContentRef" class="main overflow-hidden overflow-y-auto">
                        <RouterView />
                    </main>
                </div>
            </div>
            <FreeTrialModal />
            <LoginModal v-if="appStore.loginModalVisible" />
            <ToastMessage />
            <InstallModal />
            <PayModal />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ensureSDK360Inited } from '@/composables/useSDK360Init';
import { whenBootstrapped } from "@/utils/bootstrap";
import { track } from "@/utils/track";

const appStore = useAppStore();
const PluginsMineStore = usePluginsMineStore();

const mainContentRef = ref(null);

// tips watch
watch(
    () => appStore.currentPage,
    () => {
        if (mainContentRef.value) {
            mainContentRef.value.scrollTop = 0;
        }
    }
);

onMounted(async () => {
    document.addEventListener('keydown', handleEscape);

    // 等启动引导跑完（渠道信息 + 云控域名，见 utils/bootstrap.ts），内部已兜住异常与超时
    await whenBootstrapped();

    // 软件启动埋点：等渠道信息就绪后上报一次（不 await，失败也不影响下面的登录态恢复）
    track("appStartUp");

    // 获取c++端存储（登录态恢复，必须先于任何可能失败的网络请求执行）
    try {
        let storageeResult: any = await appStore.MyWin.WorkAiApi("client_read_data", "");
        if (storageeResult?.length) {
            let Obj = JSON.parse(appStore.base64ToStr(storageeResult));
            if (Obj?.installedPlugins) PluginsMineStore.SET_installedPlugins(Obj.installedPlugins);
            if (Obj?.UserInfo) appStore.SET_UserInfo(Obj.UserInfo);
        }
    } catch (err) {
        console.error("[启动] 读取本地存储失败，登录态未恢复", err);
    }

    // 必须放在恢复存储之后：以本机 AiPPT 安装状态为准校正插件记录。
    // 卸载 workai 会丢掉 c 端存储，但 AiPPT 还装着，这里把那条记录补回来。
    await PluginsMineStore.SYNC_AipptRecord();

    // 获取「是否展示全部功能」状态，决定侧边栏入口 + 延时更新引导（内部已兜住异常）
    await appStore.FETCH_ShowAllState();

    // 初始化 360 联运 SDK（软件启动时必须调用；失败不能影响上面的登录态恢复）。
    // 这里失败也不阻塞：下单前 useSDK360Payment 会按需补一次 init 自愈。
    await ensureSDK360Inited();
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleEscape);
});

let handleEscape = (event) => {
    if (event.key === 'Escape') {
        appStore.closeLogin();
    }
}

</script>

<style lang="less" scoped>
.app_viewport {

    .app_stage {
        // 流式铺满 CEF 盒子：始终填满容器，最小 900x600、往上不限，不做 scale 缩放（字体清晰）
        width: 100%;
        height: 100%;
        min-width: 900px;
        min-height: 600px;
    }

    .app_shell {
        background: linear-gradient(135deg, #FFE4EF 0%, #F0E4FF 35%, #E4EEFF 65%, #D4F0FF 100%);
        box-shadow: 0 16px 34px rgba(0, 0, 0, .12);
    }

    .app_body {
        flex: 1;
        min-width: 0;
        min-height: 0;
    }

    .main {
        flex: 1;
        position: relative;
        padding: 24px 28px; // 对齐 v30_2 主内容内边距
    }
}
</style>
